import {
  getAssetEvaluationDetail,
  getAssetEvaluationDraft,
  getAssetEvaluationList,
  saveAssetEvaluationDraft,
  submitAssetEvaluation
} from '@/api/user';
import {
  anchorTypeLabelMap,
  businessDirectionLabelMap,
  categoryLabelMap,
  createDefaultEvidenceFiles,
  createEmptyApplicationAccount,
  createEmptyApplicationForm,
  platformLabelMap
} from './personalShared';

const STATUS_LABEL_MAP = {
  draft: '草稿',
  pending: '待审核',
  approved: '已通过',
  rejected: '已驳回'
};

function getResponseData(response) {
  return response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function formatDateTime(value) {
  if (!value) {
    return '--';
  }

  return String(value).replace('T', ' ').slice(0, 16);
}

function getFilenameFromUrl(url) {
  if (!url) {
    return '';
  }

  const cleanUrl = String(url).split('?')[0];
  const segments = cleanUrl.split('/');
  return segments[segments.length - 1] || cleanUrl;
}

function normalizeEvidenceFiles(urls) {
  const defaults = createDefaultEvidenceFiles();
  const nextUrls = Array.isArray(urls) ? urls.filter(Boolean).slice(0, defaults.length) : [];

  return defaults.map((item, index) => {
    const url = nextUrls[index] || '';
    return {
      ...item,
      url,
      fileName: url ? getFilenameFromUrl(url) : ''
    };
  });
}

function normalizeApplicationForm(record) {
  const base = createEmptyApplicationForm();
  const source = record && typeof record === 'object' ? record : {};
  const individualData = source.individual_data && typeof source.individual_data === 'object' ? source.individual_data : {};
  const operation = individualData.operation && typeof individualData.operation === 'object' ? individualData.operation : {};
  const revenueForecast = individualData.revenue_forecast && typeof individualData.revenue_forecast === 'object' ? individualData.revenue_forecast : {};
  const platforms = Array.isArray(individualData.platforms) ? individualData.platforms : [];

  return {
    ...base,
    assetName: source.asset_name || base.assetName,
    assetCategory: source.asset_category || base.assetCategory,
    ownerName: individualData.owner_name || base.ownerName,
    ownerIdNo: individualData.owner_id_card || base.ownerIdNo,
    residence: individualData.residence || base.residence,
    honors: individualData.honors || base.honors,
    streamerType: individualData.streamer_type || base.streamerType,
    contentDirection: individualData.content_direction || base.contentDirection,
    accounts: platforms.length
      ? platforms.map((item, index) => ({
        ...createEmptyApplicationAccount(),
        id: item.id || `platform-${index}`,
        platform: item.platform || '',
        nickname: item.nickname || '',
        platformId: item.platform_id || '',
        fansCount: item.fans_count || '',
        verticalTrack: item.vertical_track || ''
      }))
      : [createEmptyApplicationAccount()],
    operation: {
      ...base.operation,
      totalFans: operation.total_fans || '',
      worksStats: operation.works_stats || '',
      liveSessionsStats: operation.live_sessions_stats || '',
      monthlyLiveHours: operation.monthly_live_hours || '',
      fanGrowth: operation.fan_growth || '',
      commercialCooperation: operation.commercial_cooperation || '',
      annualIncome: operation.annual_income || ''
    },
    evidenceFiles: normalizeEvidenceFiles(operation.evidence_files),
    forecast: {
      ...base.forecast,
      shortTerm: revenueForecast.short_term || '',
      lifecycle: revenueForecast.lifecycle || '',
      basis: revenueForecast.basis || ''
    }
  };
}

function normalizeApplicationRecord(record) {
  const source = record && typeof record === 'object' ? record : {};
  return {
    id: source.id,
    applicationNo: source.evaluate_no || '--',
    assetCategory: source.asset_category || '--',
    submittedAt: formatDateTime(source.submitted_at || source.created_at || source.updated_at),
    status: source.status || '',
    statusLabel: source.status_text || STATUS_LABEL_MAP[source.status] || source.status || '--',
    reportUrl: source.report_url || null,
    raw: source
  };
}

function buildApplicationPayload(form, sourceEvaluationId) {
  const source = form && typeof form === 'object' ? form : createEmptyApplicationForm();
  const payload = {
    asset_name: source.assetName,
    asset_category: source.assetCategory,
    individual_data: {
      owner_name: source.ownerName,
      owner_id_card: source.ownerIdNo,
      residence: source.residence,
      honors: source.honors,
      streamer_type: source.streamerType,
      content_direction: source.contentDirection,
      platforms: (Array.isArray(source.accounts) ? source.accounts : []).map(item => ({
        platform: item.platform,
        nickname: item.nickname,
        platform_id: item.platformId,
        fans_count: item.fansCount,
        vertical_track: item.verticalTrack
      })),
      operation: {
        total_fans: source.operation.totalFans,
        works_stats: source.operation.worksStats,
        live_sessions_stats: source.operation.liveSessionsStats,
        monthly_live_hours: source.operation.monthlyLiveHours,
        fan_growth: source.operation.fanGrowth,
        commercial_cooperation: source.operation.commercialCooperation,
        annual_income: source.operation.annualIncome,
        evidence_files: (Array.isArray(source.evidenceFiles) ? source.evidenceFiles : []).map(item => item.url).filter(Boolean)
      },
      revenue_forecast: {
        short_term: source.forecast.shortTerm,
        lifecycle: source.forecast.lifecycle,
        basis: source.forecast.basis
      }
    }
  };

  if (sourceEvaluationId) {
    payload.source_evaluation_id = sourceEvaluationId;
  }

  return payload;
}

function normalizeListResponse(response) {
  const data = getResponseData(response);
  if (Array.isArray(data)) {
    return data.map(normalizeApplicationRecord);
  }

  if (data && Array.isArray(data.data)) {
    return data.data.map(normalizeApplicationRecord);
  }

  return [];
}

export function getStatusLabel(status) {
  return STATUS_LABEL_MAP[status] || status || '--';
}

export function getPlatformLabel(value) {
  return platformLabelMap[value] || value || '--';
}

export function getCategoryLabel(value) {
  return categoryLabelMap[value] || value || '--';
}

export function getAnchorTypeLabel(value) {
  return anchorTypeLabelMap[value] || value || '--';
}

export function getBusinessDirectionLabel(value) {
  return businessDirectionLabelMap[value] || value || '--';
}

export async function getApplications(params = { per_page: 15 }) {
  const response = await getAssetEvaluationList(params);
  return normalizeListResponse(response);
}

export async function getApplicationById(id) {
  const response = await getAssetEvaluationDetail(id);
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return {
    ...normalizeApplicationRecord(data),
    form: normalizeApplicationForm(data)
  };
}

export async function getApplicationDraft() {
  const response = await getAssetEvaluationDraft();
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return normalizeApplicationForm(data);
}

export async function saveApplicationDraft(form, sourceEvaluationId) {
  const response = await saveAssetEvaluationDraft(buildApplicationPayload(form, sourceEvaluationId));
  const data = getResponseData(response);
  return data ? normalizeApplicationForm(data) : null;
}

export async function submitApplication(form, sourceEvaluationId) {
  const response = await submitAssetEvaluation(buildApplicationPayload(form, sourceEvaluationId));
  const data = getResponseData(response);
  if (!data) {
    return null;
  }

  return {
    ...normalizeApplicationRecord(data),
    form: normalizeApplicationForm(data)
  };
}
